import React from 'react';
import { Nav } from 'react-bootstrap';
import './LandingPageMenu.scss';
import TagManager from 'react-gtm-module';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import FacebookIcon from '../common/SocialIcons/FacebookIcon';
import InstagramIcon from '../common/SocialIcons/InstagramIcon';
import LinkedInIcon from '../common/SocialIcons/LinkedInIcon';
import { SOCIAL_LINKS } from '../../config/socialLinks';
import { localStore } from '../utils/safeStorage';

type LandingPageMenuProps = {
  onFeedbackClick: () => void;
  variant?: 'default' | 'newLandingMobile';
};

/**
 * The header menu's links (FAQ, Send Feedback, contact, legal, cookie
 * settings), social links and language switch. Send Feedback is reported
 * through `onFeedbackClick`; the host owns the modal so it outlives the menu.
 */
const LandingPageMenu = ({
  onFeedbackClick,
  variant = 'default',
}: LandingPageMenuProps) => {
  const { i18n } = useTranslation('mainMenu');
  const languageIsEnglish = i18n.language.startsWith('en');

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStore.setItem('i18nextLng', lang);
  };

  return (
    <>
      <div className={`landing-page-menu-wrapper${variant === 'newLandingMobile' ? ' landing-page-menu-wrapper--new-landing-mobile' : ''}`}>
        <div className="landing-page-menu-wrapper__content">
          <div className="landing-page-menu-flex-column">
            <div className="landing-page-menu-flex-column__links">
              <Nav.Link
                className="menuElement"
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb#ukk" : "https://info.jaakko.com/#ukk"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_what_is_jaakko_faq_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page What is Jaakko.com FAQ Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.whatIsJaakko">
                  What is Jaakko.com?
                </Trans>
              </Nav.Link>
              <Nav.Link
                className="menuElement"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_feedback_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Feedback Clicked',
                    },
                  });
                  onFeedbackClick();
                }}
              >
                <Trans i18nKey="mainMenu.feedback">Feedback</Trans>
              </Nav.Link>
              <Nav.Link
                className="menuElement"
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb#contact" : "https://info.jaakko.com/#contact"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_contact_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Contact Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.contact">Contact</Trans>
              </Nav.Link>
              <Nav.Link
                className="menuElement"
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb/terms_and_conditions" : "https://info.jaakko.com/kayttoehdot"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_terms_and_conditions_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Terms and Conditions Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.termsConditions">Terms & Conditions</Trans>
              </Nav.Link>
              <Nav.Link
                className="menuElement"
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb/privacy_policy" : "https://info.jaakko.com/privacy_policy"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_privacy_policy_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Privacy Policy Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.privacyPolicy">Privacy Policy</Trans>
              </Nav.Link>
              <Nav.Link
                className="menuElement"
                href={languageIsEnglish ? "https://info.jaakko.com/en-gb/cookies" : "https://info.jaakko.com/evasteet"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_cookies_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Cookies Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.cookies">Cookies</Trans>
              </Nav.Link>
              {/* The dialog is opened by the GetTerms widget itself, from a delegated document listener on this data attribute. */}
              <Nav.Link
                className="menuElement"
                data-gt-cookie-widget-show
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: 'landing_page_cookie_settings_clicked',
                      category: 'User Interaction',
                      action: 'Click',
                      label: 'Landing Page Cookie Settings Clicked',
                    },
                  });
                }}
              >
                <Trans i18nKey="mainMenu.cookieSettings">Cookie settings</Trans>
              </Nav.Link>
            </div>

            <div
              className="landing-page-menu-flex-column__social-links-and-language-selector"
            >
              <div className="landing-page-menu-flex-column__social-links landing-page-social-links">
                <a
                  className={`landing-page-social-links__icon ${!SOCIAL_LINKS.facebook ? 'landing-page-social-links__icon--disabled' : ''}`}
                  href={SOCIAL_LINKS.facebook || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  onClick={() => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'landing_page_social_facebook_clicked',
                        category: 'User Interaction',
                        action: 'Click',
                        label: 'Landing Page Facebook Clicked',
                      },
                    });
                  }}
                >
                  <FacebookIcon size={18} />
                </a>
                <a
                  className={`landing-page-social-links__icon ${!SOCIAL_LINKS.linkedin ? 'landing-page-social-links__icon--disabled' : ''}`}
                  href={SOCIAL_LINKS.linkedin || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  onClick={() => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'landing_page_social_linkedin_clicked',
                        category: 'User Interaction',
                        action: 'Click',
                        label: 'Landing Page LinkedIn Clicked',
                      },
                    });
                  }}
                >
                  <LinkedInIcon size={18} />
                </a>
                <a
                  className={`landing-page-social-links__icon ${!SOCIAL_LINKS.instagram ? 'landing-page-social-links__icon--disabled' : ''}`}
                  href={SOCIAL_LINKS.instagram || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  onClick={() => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'landing_page_social_instagram_clicked',
                        category: 'User Interaction',
                        action: 'Click',
                        label: 'Landing Page Instagram Clicked',
                      },
                    });
                  }}
                >
                  <InstagramIcon size={18} />
                </a>
              </div>

              <div className="menuElement landing-page-language-selector">
                <div className="landing-page-language-selector__title">
                  <Trans i18nKey="mainMenu.language">Language</Trans>
                </div>
                <div className="landing-page-language-selector__options">
                  <button
                    className={`landing-page-language-selector__option ${languageIsEnglish ? 'landing-page-language-selector__option--active' : ''}`}
                    onClick={() => {
                      TagManager.dataLayer({
                        dataLayer: {
                          event: 'landing_page_language_selector_en_clicked',
                          category: 'User Interaction',
                          action: 'Click',
                          label: 'Landing Page Language Selector EN Clicked',
                        },
                      });
                      changeLanguage('en');
                    }}
                  >
                    EN
                  </button>
                  <button
                    className={`landing-page-language-selector__option ${!languageIsEnglish ? 'landing-page-language-selector__option--active' : ''}`}
                    onClick={() => {
                      TagManager.dataLayer({
                        dataLayer: {
                          event: 'landing_page_language_selector_fi_clicked',
                          category: 'User Interaction',
                          action: 'Click',
                          label: 'Landing Page Language Selector FI Clicked',
                        },
                      });
                      changeLanguage('fi');
                    }}
                  >
                    FI
                  </button>
                </div>
              </div>

            </div>
            <div className="landing-page-menu-wrapper__copyright">
              <Trans i18nKey="mainMenu.copyright">© 2026 Jaakkocom Oy</Trans>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingPageMenu;
